import { useUser, UserButton } from "@clerk/react";
import { Link } from "react-router";
import { useUiStore } from "../../stores/useUiStore";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  string | undefined;

// Clerk's UserButton renders the avatar + a dropdown with "Manage account"
// and "Sign out". The AuthGate in App.tsx handles the signed-out state (full
// SignIn screen), so this only renders for signed-in users.
function ClerkUserAvatar() {
  const { isSignedIn, user } = useUser();

  if (!isSignedIn) return null;

  const name =
    user.fullName || user.primaryEmailAddress?.emailAddress || "User";

  return (
    <div style={styles.userBlock}>
      <span style={styles.userName}>{name.toLowerCase()}</span>
      <UserButton />
    </div>
  );
}

function UserAvatar() {
  if (!PUBLISHABLE_KEY) return null;
  return <ClerkUserAvatar />;
}

export function TopNav() {
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  return (
    <header style={styles.bar}>
      <div style={styles.left}>
        <button
          type="button"
          onClick={toggleSidebar}
          className="util-btn"
          style={styles.toggle}
          title={sidebarOpen ? "hide sidebar" : "show sidebar"}
          aria-label="toggle sidebar"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="16" rx="1.5" />
            <line x1="9" y1="4" x2="9" y2="20" />
          </svg>
        </button>
        <Link to="/" style={styles.brand}>
          shelf
        </Link>
      </div>
      <UserAvatar />
    </header>
  );
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "var(--topnav-height)",
    padding: "0 var(--space-4) 0 0",
    background: "var(--comp-paper)",
    borderBottom: "1px solid var(--comp-rule)",
  },
  left: {
    flexShrink: 0,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    padding: "0 var(--space-3)",
    height: "100%",
  },
  toggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    color: "var(--fg-3)",
    borderRadius: 0,
    flexShrink: 0,
  },
  brand: {
    fontFamily: "var(--font-mono)",
    fontWeight: 600,
    fontSize: 13,
    color: "var(--fg-1)",
    letterSpacing: 0,
    textDecoration: "none",
  },
  loginBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--fg-2)",
    padding: "2px 4px",
    background: "transparent",
    cursor: "pointer",
  },
  userBlock: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    cursor: "default",
  },
  userName: {
    fontSize: 12,
    color: "var(--fg-3)",
  },
  avatar: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    objectFit: "cover",
    border: "1px solid var(--comp-rule)",
  },
  avatarFallback: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--comp-ink)",
    color: "var(--fg-on-deep)",
    fontSize: 11,
    fontWeight: 600,
  },
};
